import React, { useCallback, useEffect, useState } from "react";
import { api } from "../../../software/api";
import { Wallet, Lock, Unlock, Plus, TrendingDown } from "lucide-react";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export default function CashDrawer() {
  const [state, setState] = useState(null);
  const [floatAmt, setFloatAmt] = useState("");
  const [counted, setCounted] = useState("");
  const [petty, setPetty] = useState({ amount: "", reason: "" });
  const [result, setResult] = useState(null);

  const load = useCallback(async () => setState(await api("GET", "/cashdrawer/current")), []);
  useEffect(() => { load(); }, [load]);

  const open = async (e) => {
    e.preventDefault();
    await api("POST", "/cashdrawer/open", { float_amount: Number(floatAmt) || 0 });
    setFloatAmt(""); setResult(null); load();
  };
  const addPetty = async (e) => {
    e.preventDefault();
    if (!petty.amount) return;
    await api("POST", "/cashdrawer/petty", { amount: Number(petty.amount) || 0, reason: petty.reason });
    setPetty({ amount: "", reason: "" }); load();
  };
  const close = async (e) => {
    e.preventDefault();
    const r = await api("POST", "/cashdrawer/close", { counted: Number(counted) || 0 });
    setResult(r); setCounted(""); load();
  };

  if (!state) return null;
  const s = state.session;

  return (
    <div data-testid="cashdrawer-panel" className="max-w-2xl">
      <h1 className="font-display text-2xl font-bold text-slate-900 mb-1">Cash drawer</h1>
      <p className="text-sm text-slate-500 mb-5">Open a session with a float, log petty-cash withdrawals, then count and close to see any discrepancy.</p>

      {!state.open ? (
        <form onSubmit={open} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="drawer-closed">
          <div className="flex items-center gap-2 mb-3 text-slate-700"><Lock className="h-5 w-5" /> <span className="font-semibold">Drawer is closed</span></div>
          <label className="block text-xs font-medium text-slate-600 mb-1">Opening float ($)</label>
          <div className="flex gap-2">
            <input type="number" step="any" value={floatAmt} onChange={(e) => setFloatAmt(e.target.value)} data-testid="drawer-float-input" className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" placeholder="e.g. 200" />
            <button data-testid="drawer-open-btn" className="inline-flex items-center gap-2 rounded-full bg-emerald-600 hover:bg-emerald-700 text-white text-sm font-semibold px-5 transition-colors"><Unlock className="h-4 w-4" /> Open drawer</button>
          </div>
          {result && (
            <div className="mt-4 rounded-xl bg-slate-50 border border-slate-100 p-4 text-sm" data-testid="drawer-close-result">
              <p className="font-semibold text-slate-800 mb-1">Last session closed</p>
              <div className="flex justify-between"><span className="text-slate-500">Expected</span><span className="font-mono2">{money(result.expected)}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Counted</span><span className="font-mono2">{money(result.counted)}</span></div>
              <div className={`flex justify-between font-bold ${Math.abs(result.discrepancy) < 0.01 ? "text-emerald-600" : "text-rose-600"}`}><span>Discrepancy</span><span className="font-mono2">{money(result.discrepancy)}</span></div>
            </div>
          )}
        </form>
      ) : (
        <div className="space-y-4">
          <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5" data-testid="drawer-open">
            <div className="flex items-center gap-2 mb-3 text-emerald-800"><Wallet className="h-5 w-5" /> <span className="font-semibold">Drawer open</span> <span className="text-xs text-emerald-600">since {new Date(s.opened_at).toLocaleString()}</span></div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div><p className="text-xs text-slate-500">Opening float</p><p className="font-mono2 font-bold text-slate-800">{money(s.opening_float)}</p></div>
              <div><p className="text-xs text-slate-500">Cash sales</p><p className="font-mono2 font-bold text-slate-800">{money(s.cash_total)}</p></div>
              <div><p className="text-xs text-slate-500">Petty cash</p><p className="font-mono2 font-bold text-slate-800">-{money(state.petty_total)}</p></div>
              <div><p className="text-xs text-slate-500">Expected in drawer</p><p className="font-mono2 font-bold text-emerald-700" data-testid="drawer-expected">{money(state.expected)}</p></div>
            </div>
          </div>

          <form onSubmit={addPetty} className="rounded-2xl border border-slate-200 bg-white p-5 flex flex-wrap items-end gap-3">
            <div className="flex items-center gap-2 w-full text-slate-700 mb-1"><TrendingDown className="h-4 w-4" /> <span className="font-semibold text-sm">Petty cash out</span></div>
            <div><label className="block text-xs text-slate-600 mb-1">Amount ($)</label><input type="number" step="any" value={petty.amount} onChange={(e) => setPetty({ ...petty, amount: e.target.value })} data-testid="petty-amount" className="w-28 rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" /></div>
            <div className="flex-1 min-w-[160px]"><label className="block text-xs text-slate-600 mb-1">Reason</label><input value={petty.reason} onChange={(e) => setPetty({ ...petty, reason: e.target.value })} data-testid="petty-reason" className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm outline-none focus:border-sky-400" placeholder="e.g. milk & sugar" /></div>
            <button data-testid="petty-add-btn" className="inline-flex items-center gap-1.5 rounded-full bg-slate-900 hover:bg-sky-700 text-white text-sm font-semibold px-4 py-2 transition-colors"><Plus className="h-4 w-4" /> Log</button>
          </form>

          {(s.petty || []).length > 0 && (
            <div className="rounded-2xl border border-slate-200 bg-white divide-y divide-slate-100">
              {s.petty.map((p, i) => <div key={i} className="flex justify-between px-4 py-2.5 text-sm"><span className="text-slate-600">{p.reason || "—"} <span className="text-slate-400 text-xs">· {p.taken_by}</span></span><span className="font-mono2 text-rose-600">-{money(p.amount)}</span></div>)}
            </div>
          )}

          <form onSubmit={close} className="rounded-2xl border border-slate-200 bg-white p-5">
            <label className="block text-xs font-medium text-slate-600 mb-1">Counted cash at close ($)</label>
            <div className="flex gap-2">
              <input type="number" step="any" value={counted} onChange={(e) => setCounted(e.target.value)} data-testid="drawer-counted-input" className="flex-1 rounded-xl border border-slate-200 px-3 py-2.5 text-sm outline-none focus:border-sky-400" placeholder={`expected ${money(state.expected)}`} />
              <button data-testid="drawer-close-btn" className="inline-flex items-center gap-2 rounded-full bg-slate-900 hover:bg-rose-700 text-white text-sm font-semibold px-5 transition-colors"><Lock className="h-4 w-4" /> Close & reconcile</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
